import { createTRPCContext } from "@trpc/tanstack-react-query";
import { QueryClient } from "@tanstack/react-query";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@api-types/routers/routers";

import { shouldRetryQuery } from "@/lib/trpc-errors";

export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

/** Derived input types of every procedure — prefer these over re-deriving per file. */
export type RouterInputs = inferRouterInputs<AppRouter>;

/** Derived output types of every procedure — prefer these over re-deriving per file. */
export type RouterOutputs = inferRouterOutputs<AppRouter>;

/**
 * The input object of an optional-input procedure (one declared as
 * `z.object({...}).optional()` — the house pattern for filter-only list queries).
 * tRPC unions `void` into such inputs so bare calls type-check, and `NonNullable`
 * strips only `null | undefined` — hence the explicit `Exclude`. Use this instead
 * of rediscovering that trap at each call site:
 *
 *   type ListInput = DefinedInput<RouterInputs["admin"]["listAppealedIncidents"]>;
 */
export type DefinedInput<T> = Exclude<T, void | undefined>;

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
        retry: shouldRetryQuery,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  // Server: always create a new QueryClient for each request
  if (typeof window === "undefined") {
    return makeQueryClient();
  }

  // Browser: use singleton pattern to avoid recreating on every render
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }
  return browserQueryClient;
}
